// CDE-specific shadow schema candidate row types, mirrored 1:1 against
// migrations/0001_c1_shadow_foundation.sql. Column names there are
// snake_case; the fields below are the camelCase TypeScript shape that a
// caller decodes a stored `*_json` payload into.
//
// Every table that carries an `unknown_fields_json` column decodes with
// the same discipline as src/c1/modelChunks.ts: unknown/extension keys are
// kept verbatim (including a literal `"__proto__"` key, which JSON.parse
// produces as ordinary own-enumerable data, not a prototype write), and a
// malformed payload decodes to `undefined` rather than throwing or
// silently coercing.

import { referenceStates, type ReferenceState } from "../../domain/types";
import { isRecord, unknownProperties } from "../../shared/record";
import { isSha256Hex } from "../../shared/hash";
import type { D1LikeRow } from "./storage";

export interface DocumentVersionRow extends D1LikeRow {
  documentId: string;
  version: number;
  title: string;
  kind: string;
  ownerSubject: string;
  contentSha256: string;
  byteCount: number;
  createdAt: string;
  unknownFields: Readonly<Record<string, unknown>>;
}

export const issuePriorities = ["critical", "high", "normal", "low"] as const;
export type IssuePriority = (typeof issuePriorities)[number];

export const issueWorkflowStates = ["open", "in-review", "closed"] as const;
export type IssueWorkflowState = (typeof issueWorkflowStates)[number];

// `version` backs the C2 optimistic-concurrency guard (see
// migrations/0002_c2_state_machine_and_optimistic_guard.sql and
// backup.ts's updateRecordWithGuard) -- it is not a document/content
// revision number, unlike DocumentVersionRow.version above, which is an
// unrelated field on an unrelated table that happens to share the English
// word "version". Every row's version starts at 1 and increments by
// exactly 1 per successful PATCH; a caller never sets it directly.
export interface ShadowIssueRow extends D1LikeRow {
  externalIdentifier: string;
  title: string;
  topicType: string;
  priority: IssuePriority;
  assigneeSubject: string;
  dueDate: string;
  workflowState: IssueWorkflowState;
  federationSnapshotId?: string;
  createdAt: string;
  version: number;
  unknownFields: Readonly<Record<string, unknown>>;
}

export const rfiStatuses = ["open", "answered", "overdue"] as const;
export type RfiStatus = (typeof rfiStatuses)[number];

export interface RfiRow extends D1LikeRow {
  subject: string;
  raisedBySubject: string;
  ownerSubject: string;
  dueDate: string;
  status: RfiStatus;
  createdAt: string;
  version: number;
  unknownFields: Readonly<Record<string, unknown>>;
}

export const approvalStatuses = ["awaiting-review", "changes-requested", "approved"] as const;
export type ApprovalStatus = (typeof approvalStatuses)[number];

export interface ApprovalRow extends D1LikeRow {
  subject: string;
  package: string;
  submittedBySubject: string;
  submittedAt: string;
  status: ApprovalStatus;
  version: number;
  unknownFields: Readonly<Record<string, unknown>>;
}

export interface FederationSnapshotRow extends D1LikeRow {
  label: string;
  revisionBarrier: string;
  completeness: "complete" | "partial";
  createdAt: string;
  unknownFields: Readonly<Record<string, unknown>>;
}

// Opaque pointer only: (projectId, modelId, positiveRevision) identifies a
// BIM-Hub ModelRevision the same way src/c1/modelChunks.ts's
// ModelRevisionTuple does. No model content is stored alongside it.
export interface FederationSnapshotReferenceRow extends D1LikeRow {
  snapshotId: string;
  projectId: string;
  modelId: string;
  positiveRevision: number;
  referenceState: ReferenceState;
  manifestDigest?: string;
  unknownFields: Readonly<Record<string, unknown>>;
}

export function decodeDocumentVersionRow(value: unknown): DocumentVersionRow | undefined {
  if (!isRecord(value)) return undefined;
  const { id, documentId, version, title, kind, ownerSubject, contentSha256, byteCount, createdAt } = value;
  if (typeof id !== "string" || typeof documentId !== "string" || typeof title !== "string"
    || typeof kind !== "string" || typeof ownerSubject !== "string" || typeof createdAt !== "string") return undefined;
  if (typeof version !== "number" || !Number.isInteger(version) || version <= 0) return undefined;
  if (typeof contentSha256 !== "string" || !isSha256Hex(contentSha256)) return undefined;
  if (typeof byteCount !== "number" || !Number.isSafeInteger(byteCount) || byteCount < 0) return undefined;

  const unknownFields = unknownProperties(value, [
    "id", "documentId", "version", "title", "kind", "ownerSubject", "contentSha256", "byteCount", "createdAt",
  ]);
  return { id, documentId, version, title, kind, ownerSubject, contentSha256, byteCount, createdAt, unknownFields };
}

// Decodes the optimistic-concurrency `version` field shared by
// ShadowIssueRow/RfiRow/ApprovalRow. Absent is accepted and defaults to 1
// -- a POST create body predates this column's existence for most callers
// (fixtures, BCF import, every caller written before migration 0002) and
// a freshly-created row always legitimately starts at version 1 anyway,
// so requiring the field on create would only add client-side boilerplate
// for a value the server determines regardless. A *present* value must be
// a positive integer; anything else (a string, 0, a float) is rejected
// rather than silently coerced, matching every other decode*Row field
// here. `undefined` return distinguishes "rejected" from "1" (a valid
// value) for the caller below.
function decodeOptimisticVersion(value: unknown): number | undefined {
  if (value === undefined) return 1;
  if (typeof value !== "number" || !Number.isInteger(value) || value <= 0) return undefined;
  return value;
}

export function decodeShadowIssueRow(value: unknown): ShadowIssueRow | undefined {
  if (!isRecord(value)) return undefined;
  const {
    id, externalIdentifier, title, topicType, priority, assigneeSubject, dueDate, workflowState,
    federationSnapshotId, createdAt, version,
  } = value;
  if (typeof id !== "string" || typeof externalIdentifier !== "string" || typeof title !== "string"
    || typeof topicType !== "string" || typeof assigneeSubject !== "string" || typeof dueDate !== "string"
    || typeof createdAt !== "string") return undefined;
  if (typeof priority !== "string" || !isOneOf(priority, issuePriorities)) return undefined;
  if (typeof workflowState !== "string" || !isOneOf(workflowState, issueWorkflowStates)) return undefined;
  if (federationSnapshotId !== undefined && typeof federationSnapshotId !== "string") return undefined;
  const decodedVersion = decodeOptimisticVersion(version);
  if (decodedVersion === undefined) return undefined;

  const unknownFields = unknownProperties(value, [
    "id", "externalIdentifier", "title", "topicType", "priority", "assigneeSubject", "dueDate", "workflowState",
    "federationSnapshotId", "createdAt", "version",
  ]);
  return {
    id, externalIdentifier, title, topicType, priority, assigneeSubject, dueDate, workflowState,
    federationSnapshotId, createdAt, version: decodedVersion, unknownFields,
  };
}

export function decodeRfiRow(value: unknown): RfiRow | undefined {
  if (!isRecord(value)) return undefined;
  const { id, subject, raisedBySubject, ownerSubject, dueDate, status, createdAt, version } = value;
  if (typeof id !== "string" || typeof subject !== "string" || typeof raisedBySubject !== "string"
    || typeof ownerSubject !== "string" || typeof dueDate !== "string" || typeof createdAt !== "string") return undefined;
  if (typeof status !== "string" || !isOneOf(status, rfiStatuses)) return undefined;
  const decodedVersion = decodeOptimisticVersion(version);
  if (decodedVersion === undefined) return undefined;

  const unknownFields = unknownProperties(value, [
    "id", "subject", "raisedBySubject", "ownerSubject", "dueDate", "status", "createdAt", "version",
  ]);
  return { id, subject, raisedBySubject, ownerSubject, dueDate, status, createdAt, version: decodedVersion, unknownFields };
}

export function decodeApprovalRow(value: unknown): ApprovalRow | undefined {
  if (!isRecord(value)) return undefined;
  const { id, subject, package: pkg, submittedBySubject, submittedAt, status, version } = value;
  if (typeof id !== "string" || typeof subject !== "string" || typeof pkg !== "string"
    || typeof submittedBySubject !== "string" || typeof submittedAt !== "string") return undefined;
  if (typeof status !== "string" || !isOneOf(status, approvalStatuses)) return undefined;
  const decodedVersion = decodeOptimisticVersion(version);
  if (decodedVersion === undefined) return undefined;

  const unknownFields = unknownProperties(value, [
    "id", "subject", "package", "submittedBySubject", "submittedAt", "status", "version",
  ]);
  return { id, subject, package: pkg, submittedBySubject, submittedAt, status, version: decodedVersion, unknownFields };
}

export function decodeFederationSnapshotRow(value: unknown): FederationSnapshotRow | undefined {
  if (!isRecord(value)) return undefined;
  const { id, label, revisionBarrier, completeness, createdAt } = value;
  if (typeof id !== "string" || typeof label !== "string" || typeof revisionBarrier !== "string"
    || typeof createdAt !== "string") return undefined;
  if (completeness !== "complete" && completeness !== "partial") return undefined;

  const unknownFields = unknownProperties(value, ["id", "label", "revisionBarrier", "completeness", "createdAt"]);
  return { id, label, revisionBarrier, completeness, createdAt, unknownFields };
}

export function decodeFederationSnapshotReferenceRow(value: unknown): FederationSnapshotReferenceRow | undefined {
  if (!isRecord(value)) return undefined;
  const { id, snapshotId, projectId, modelId, positiveRevision, referenceState, manifestDigest } = value;
  if (typeof id !== "string" || typeof snapshotId !== "string" || typeof projectId !== "string"
    || typeof modelId !== "string") return undefined;
  if (typeof positiveRevision !== "number" || !Number.isSafeInteger(positiveRevision) || positiveRevision <= 0) return undefined;
  if (typeof referenceState !== "string" || !isOneOf(referenceState, referenceStates)) return undefined;
  if (manifestDigest !== undefined && (typeof manifestDigest !== "string" || !isSha256Hex(manifestDigest))) return undefined;

  const unknownFields = unknownProperties(value, [
    "id", "snapshotId", "projectId", "modelId", "positiveRevision", "referenceState", "manifestDigest",
  ]);
  return { id, snapshotId, projectId, modelId, positiveRevision, referenceState, manifestDigest, unknownFields };
}

function isOneOf<T extends string>(value: string, options: readonly T[]): value is T {
  return (options as readonly string[]).includes(value);
}
